import React from "react"
import Reveal from "./Reveal.jsx"

const ENTRIES = [
  {
    q: "How does the free trial work?",
    a: "Sign up with your email and a payment card; the card is verified but never charged during the 30-day trial. Use everything, every feature, no caps. Cancel any time during the trial and your card is never charged. If you don't cancel, the card is automatically charged $99 on day 31 and your subscription begins. One trial per email and per webshop URL.",
  },
  {
    q: "Why do you require a card to start the trial?",
    a: "AI inference, hosting, and CDN delivery all cost real money. A card requirement protects honest customers from trial-abuse fraud and lets us offer 30 days unrestricted instead of a watered-down preview. You can cancel any time — the card is never charged during the trial.",
  },
  {
    q: "Can I cancel any time?",
    a: "Yes. Cancellation is self-service from the dashboard. During the trial, cancellation means your card is never charged. After the trial, cancellation takes effect at the end of your current billing month — you keep access to month-end and you're not billed again. No long-term contract, no exit fees. Winners already shipped through the snippet keep working; you can export them as plain DOM changes before you leave.",
  },
  {
    q: "Do you offer refunds after the trial ends?",
    a: "We don't pro-rate refunds for the current billing month, but you'll never be billed again the moment you cancel. EU consumers retain the statutory 14-day right of withdrawal — see the Cancellation policy for details. The 30-day free trial gives you a longer window than the statutory minimum anyway.",
  },
  {
    q: "How does ShopShift handle GDPR?",
    a: "Your shop is responsible for visitor consent (cookie banner, TCF, etc.). The snippet checks the standard signals on every page load — `navigator.globalPrivacyControl`, the IAB TCF API — and stays silent if consent isn't there. We never persist visitor IPs; country is derived at the request boundary and discarded. Raw visit and event data is purged after 90 days; only aggregates are kept.",
  },
  {
    q: "Will the snippet slow my shop down?",
    a: "The snippet ships as a sub-20kb gzipped file from a CDN with stale-while-origin-offline caching, loads asynchronously, and never blocks your page render. Anti-flicker has a hard 3-second safety timeout — if our backend is unreachable, your shop renders normally and we simply don't run experiments that pageview.",
  },
  {
    q: "What does the AI actually change on my site?",
    a: "Whatever you authorize, and nothing else. You pick a scope — conservative (text only), moderate (text + visual styles), or aggressive (full DOM). On top of that you can name protected selectors and protected URL paths the AI will never touch — checkout forms, account pages, anything else you mark off-limits. Every proposal passes a Ruby validator before it ever runs.",
  },
  {
    q: "What changes does the AI never make?",
    a: "It will not touch payment forms, checkout flows, or anything you've marked protected. It will not modify pricing, inventory, or product data. It will not write to your database, send emails, or call third-party APIs on your behalf. The AI only proposes DOM-level visual and copy changes that the snippet applies in the visitor's browser.",
  },
  {
    q: "How long is data kept?",
    a: "Raw visits and events are kept for 90 days, then purged. Aggregated rollups (daily stats, per-page behavior, experiment results, winners) are kept indefinitely so your historical chart never loses data. Your shop profile and verified facts are kept until you delete the shop.",
  },
  {
    q: "What platforms does it work on?",
    a: "Anything that lets you paste a script tag — Shopify, WooCommerce, Magento, custom Rails, Next.js, you name it. Single-page apps are supported via MutationObserver and History API interception. Platform-specific plugins (Shopify app, etc.) are on the V2 roadmap.",
  },
]

function FaqItem({ q, a }) {
  return (
    <details className="group border-b border-[color:var(--color-rule)] py-6">
      <summary
        className="flex items-baseline justify-between gap-6 cursor-pointer list-none font-sans text-[18px] tracking-[-0.02em] font-medium text-[color:var(--color-ink-primary)] leading-[1.4]"
        style={{ outline: "none" }}
      >
        <span>{q}</span>
        <span
          aria-hidden
          className="font-mono text-[14px] text-[color:var(--color-ink-tertiary)] transition-transform duration-200 group-open:rotate-45 flex-shrink-0"
          style={{ lineHeight: 1 }}
        >
          +
        </span>
      </summary>
      <p className="mt-4 max-w-3xl font-sans text-[15px] text-[color:var(--color-ink-secondary)] leading-[1.65]">
        {a}
      </p>
    </details>
  )
}

export default function Faq() {
  return (
    <section
      id="faq"
      className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-surface-card)]"
    >
      <div className="max-w-6xl mx-auto px-6 py-32">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-4">
            § 06 · FAQ
          </p>
          <h2 className="font-sans text-[40px] tracking-[-0.035em] font-medium leading-[1.1] max-w-2xl">
            Things people usually ask first.
          </h2>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-16 max-w-4xl border-t border-[color:var(--color-rule-strong)]">
            {ENTRIES.map((entry) => (
              <FaqItem key={entry.q} q={entry.q} a={entry.a} />
            ))}
          </div>
        </Reveal>

        <Reveal delay={240}>
          <p className="mt-12 font-sans text-[14px] text-[color:var(--color-ink-secondary)] leading-[1.6]">
            Still unsure?{" "}
            <a
              href="mailto:support@shopshift.io"
              className="font-medium text-[color:var(--color-ai)] hover:underline"
            >
              support@shopshift.io
            </a>
            {" "}— a real human reads every email.
          </p>
        </Reveal>
      </div>
    </section>
  )
}
